// =============================================================================
// io/sync/restoreAccount.ts — "Import a backup" and "Reset Everything".
// -----------------------------------------------------------------------------
// The two destructive whole-book controls, and the mirror of `exportAccount.ts`.
// Read that file first: this one restores exactly what that one writes, shares
// its table list, and inherits its exclusions as HOLES that have to be named.
//
// ── 🚨 WHY THIS IS NOT BETA'S IMPORT, AND COULD NOT BE ──
//
// Beta's whole book is one in-memory object. Export is `JSON.stringify(state)`,
// import is `state = migrateState(parsed)` followed by one diff-based save
// (app/index.html:50256). There is no such object here: the book is eighteen
// tables behind RLS.
//
// ⚠ AND THE CARD'S PREMISE — "this book is behind a typed model D62 calls
// lossy" — DOES NOT APPLY TO THIS PATH. It would, if a restore round-tripped
// through `JournalEntry` and friends: D62 measured that doing so zeroes four
// self-assessment ratings, every hypothesis→pattern link and six contract
// timestamps. It does not, because `exportAccount` reads `select *` — raw rows,
// never the typed model — and this file writes those same raw rows back. The
// model is not in the path in either direction. A test pins that with a column
// no typed model owns.
//
// ── 🔴 THE THREE THINGS A RESTORE CANNOT GIVE BACK, ALL VERIFIED ──
//
// 1. **INGEST KEYS. `key_hash` is `TEXT NOT NULL UNIQUE`**
//    (20260616000001_ingest_keys.sql:17) **and the export withholds it on
//    purpose** — it is the sha-256 of a live key and has no business in a file
//    in someone's Downloads. So the rows in the file cannot be re-inserted:
//    the column is required and the value is gone.
//
//    🚨 THE CONSEQUENCE IF THIS TABLE WERE TREATED LIKE THE OTHERS: a "replace
//    everything" would delete the user's live ATAS/Sierra keys and restore
//    nothing. Their loggers keep POSTing, the Edge Function stops recognising
//    them, and **trades silently stop arriving** — no error on any screen. That
//    is the exact failure PROJECT_PLAN warns about for the cutover, reached by
//    a button in Settings instead.
//
//    ▶ So `ingest_keys` is NEITHER wiped NOR restored. It is left alone, and
//    the screen says so before the user commits.
//
// 2. **Storage objects.** Screenshots, recordings and snapshot images are files,
//    not rows. The export is JSON and says so in its own exclusions. Restored
//    rows keep their filenames, so a restore into the same account still finds
//    them — into a DIFFERENT one it does not.
//
// 3. **The subscription.** Held by the payment provider and excluded on purpose.
//
// ── 🚨 IMPORT UPSERTS FIRST AND DELETES LAST. IT NEVER PASSES THROUGH EMPTY ──
//
// There is no transaction available here. Whole-account deletion in this project
// is a service-role Edge Function precisely because the browser cannot have one
// (`supabase/functions/account-purge`), and this runs on the anon key under RLS.
// So a restore is dozens of separate requests that can stop at any point.
//
// The obvious shape — wipe, then insert — has a window in which the book is
// GONE and the replacement has not landed. A dropped connection there is
// unrecoverable from the app: the user is looking at an empty account and the
// only copy is a file they must think to re-import.
//
// ▶ Upsert-then-prune has the same END STATE and no such window. Halfway through
// a failed run the user holds a SUPERSET — their old rows plus the file's —
// which is visible, still theirs, and fixed by running it again. Beta's
// diff-based save has this property too, for the same reason
// (app/index.html:5212); this is that property, ported.
//
// ── ⚠ RESET IS THE EASIER ONE, AND ONLY BECAUSE IT IS IDEMPOTENT ──
//
// It is equally non-atomic, but every step is a delete, so a partial run is
// repaired by pressing the button again. It still reports which tables failed
// rather than claiming success — beta's handler at `:50282` says the same, and
// its comment records what happened when it did not.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';
import { EXPORT_FORMAT, EXPORT_TABLES } from './exportAccount';
import { record, reportFailureAs } from '../diagnostics/errorLog';

/**
 * Tables in the export that this module will not touch, and why.
 *
 * ⚠ EXPORTED AND RENDERED ON SCREEN. A destructive control that quietly skips
 * part of what it claims to replace is the same defect as an export that
 * quietly omits a table — which is what `exportAccount`'s own header was written
 * about. The user reads this list before confirming.
 */
export const RESTORE_SKIPPED: readonly { table: string; why: string }[] = [
  {
    table: 'ingest_keys',
    why: 'your logger keys are left exactly as they are — the backup file deliberately '
      + 'does not contain the secret half, so they could be destroyed but never restored',
  },
  /**
   * ⚠ SAME SHAPE AS `ingest_keys`, SAME REASON: the file holds the record of the
   * thing but not the thing itself.
   *
   * A share link is served by an Edge Function that reads `payload` — the
   * rendered report — and the export withholds that column deliberately. So a
   * restored row would be a live public URL that resolves to nothing, which
   * reads to whoever was sent it as the product being broken rather than as a
   * link that was replaced.
   *
   * ▶ The links are still IN the file, with their created, expiry and revoked
   * dates, because "what did I send out" is worth having written down. They are
   * simply not something this control can put back.
   */
  {
    table: 'shared_reports',
    why: 'the read-only links you have shared are left exactly as they are — the backup '
      + 'records which links exist but not the report each one serves, so restoring a row '
      + 'would republish a link that resolves to nothing',
  },
];

const SKIPPED = new Set(RESTORE_SKIPPED.map((s) => s.table));

/**
 * Every table this module writes or clears, PARENTS FIRST.
 *
 * ── ⚠ THE ORDER IS THE FOREIGN-KEY GRAPH, READ OUT OF THE MIGRATIONS ──
 *
 *   trades.account_id            → accounts       ON DELETE SET NULL
 *   trades.pattern_id            → patterns       ON DELETE SET NULL
 *   trades.exit_model_id         → exit_models    ON DELETE SET NULL
 *   account_adjustments.account_id → accounts     ON DELETE CASCADE
 *   best_opps.pattern_id         → patterns       ON DELETE SET NULL
 *   best_opps.trade_id           → trades         ON DELETE SET NULL
 *   practice_trades.session_id   → practice_sessions ON DELETE CASCADE
 *
 * ⚠ `trades.contract` USED to be `NOT NULL REFERENCES contracts(symbol)`, which
 * would have made this impossible for anyone with a per-user instrument: the
 * shared catalogue is excluded from the export, so the parent row would not be
 * in the file. **D256 dropped that constraint** (20260906000006_user_contracts)
 * — checked, because the initial schema still shows it and reading only that
 * file would have produced a confident wrong answer about a hard blocker.
 */
export const RESTORE_ORDER: readonly string[] = [
  'user_preferences',
  'commission_schedules',
  'accounts',
  'patterns',
  'exit_models',
  'practice_sessions',
  'trades',
  'account_adjustments',
  'best_opps',
  'practice_trades',
  'goals',
  'journal_entries',
  'reviews',
  'backtests',
  'snapshots',
  'user_contracts',
  'subscriptions',
];

/** Children first — the reverse of the write order. */
export const WIPE_ORDER: readonly string[] = [...RESTORE_ORDER].reverse();

/**
 * The primary key each table is upserted and pruned on.
 *
 * ⚠ TAKEN FROM `EXPORT_TABLES[].orderBy`, which its own header records as read
 * out of `information_schema` on the linked database rather than assumed. Three
 * of these are composite `(user_id, …)`; RLS scopes every row to one user, so
 * the other half identifies a row on its own — the same argument the export
 * makes for ordering by it.
 */
const KEY_OF: Record<string, string> = Object.fromEntries(
  EXPORT_TABLES.map((t) => [t.table, t.orderBy]),
);

/** A table this module handles, in file order. */
export interface RestorePlanEntry {
  table: string;
  rows: Record<string, unknown>[];
}

export interface FileProblem {
  /** `blocking` refuses the import outright. `warning` is shown and continues. */
  severity: 'blocking' | 'warning';
  message: string;
}

export interface ValidatedFile {
  ok: boolean;
  problems: FileProblem[];
  plan: RestorePlanEntry[];
  /** Total rows the file will write. */
  rowCount: number;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Read the file and decide whether it may be restored at all.
 *
 * ── 🚨 A SHORT FILE IS THE ONE THAT MUST BE REFUSED ──
 *
 * `exportAccount` replaces an unreadable table with `{_error: …}` rather than
 * failing the whole export — correct for a download, because sixteen good
 * tables beat none. **On the way back in it is the opposite.** A stub is
 * indistinguishable from "you had no trades", so restoring one would prune every
 * real row in that table against an empty list and call it a replacement.
 *
 * ⚠ THAT IS THE ONLY WAY THIS FEATURE CAN DESTROY DATA THAT THE USER DID NOT
 * ASK IT TO. Everything else here is doing what the button says.
 */
export function validateImportFile(
  parsed: unknown,
  user: { id: string },
): ValidatedFile {
  const problems: FileProblem[] = [];
  const plan: RestorePlanEntry[] = [];

  if (!isObj(parsed)) {
    return {
      ok: false,
      problems: [{ severity: 'blocking', message: 'That file isn’t a TRADECOACH backup — it doesn’t contain an object.' }],
      plan: [],
      rowCount: 0,
    };
  }

  const meta = isObj(parsed['meta']) ? parsed['meta'] : null;
  const format = meta ? String(meta['format'] ?? '') : '';
  if (format !== EXPORT_FORMAT) {
    problems.push({
      severity: 'blocking',
      message: format
        ? `That file says it is “${format}”, and this app reads “${EXPORT_FORMAT}”.`
        : 'That file isn’t a TRADECOACH backup — it has no format stamp.',
    });
  }

  // ⚠ A DIFFERENT ACCOUNT IS A WARNING, NOT A REFUSAL, and the rows are
  // re-stamped below. Restoring your own book into a NEW account — after a
  // deletion, say — is the case a backup exists for, and the user ids will never
  // match there. Refusing it would make the file useless in the one situation it
  // was taken for.
  const fileUser = meta ? String(meta['user_id'] ?? '') : '';
  if (fileUser && fileUser !== user.id) {
    problems.push({
      severity: 'warning',
      message: 'This backup was taken from a different account. Its rows will be '
        + 'restored into the account you are signed in to now.',
    });
  }

  for (const table of RESTORE_ORDER) {
    const value = parsed[table];
    if (value === undefined) {
      // ⚠ Absent, not empty. An older file predates the table; nothing is
      // claimed about it, so nothing is pruned.
      problems.push({
        severity: 'warning',
        message: `“${table}” isn’t in this file — those rows will be left as they are.`,
      });
      continue;
    }
    if (isObj(value) && '_error' in value) {
      problems.push({
        severity: 'blocking',
        message: `“${table}” failed to read when this backup was taken, so the file `
          + 'does not contain it. Restoring would delete what you have now and put '
          + 'nothing back. Take a fresh backup first.',
      });
      continue;
    }
    if (!Array.isArray(value)) {
      problems.push({
        severity: 'blocking',
        message: `“${table}” is not a list of rows — this file is damaged.`,
      });
      continue;
    }
    plan.push({ table, rows: value.filter(isObj) });
  }

  const blocking = problems.some((p) => p.severity === 'blocking');
  return {
    ok: !blocking && plan.length > 0,
    problems,
    plan,
    rowCount: plan.reduce((n, e) => n + e.rows.length, 0),
  };
}

export interface TableFailure {
  table: string;
  message: string;
}

export interface WriteReport {
  /** Rows upserted, per table. */
  written: number;
  /** Rows removed because the file did not contain them. */
  pruned: number;
  /** Tables that did not complete. EMPTY IS THE ONLY SUCCESS. */
  failures: TableFailure[];
}

/** Every id currently stored for one table, paged. */
async function existingIds(
  sb: SupabaseClient, table: string, key: string,
): Promise<string[]> {
  const ids: string[] = [];
  for (let from = 0; ; from += PAGE_ROWS) {
    // ⚠ ORDERED, for the reason `exportAccount.readAll` states at length: an
    // unordered `range()` has no defined page boundary, so a row written
    // mid-read shifts the window and one id is dropped. A DROPPED ID HERE IS
    // NOT PRUNED — the mirror-image mistake is an id read twice, which is
    // harmless. Both are avoided by ordering.
    const { data, error } = await sb
      .from(table)
      .select(key)
      .order(key, { ascending: true })
      .range(from, from + PAGE_ROWS - 1);
    if (error) throw new Error(error.message);
    // ⚠ Through `unknown`. A dynamic `select(key)` gives supabase-js no column
    // to infer, so it types the result as its parse-failure shape.
    const page = (data ?? []) as unknown as Record<string, unknown>[];
    if (!page.length) break;
    for (const r of page) {
      const v = r[key];
      if (v !== null && v !== undefined) ids.push(String(v));
    }
    if (page.length < PAGE_ROWS) break;
  }
  return ids;
}

/**
 * Rows go up in chunks.
 *
 * ⚠ `supabase-js` unions the KEYS of an array payload into one `columns=`
 * parameter, so a row missing a key another row has is written as that column's
 * DEFAULT — the trap D62 names. Chunking cannot fix that on its own, so every
 * chunk is normalised to one key set first: the union of the keys across the
 * whole table's rows, with anything absent explicitly `null`.
 */
const CHUNK = 500;

function normaliseRows(
  rows: Record<string, unknown>[], userId: string,
): Record<string, unknown>[] {
  const keys = new Set<string>();
  for (const r of rows) for (const k of Object.keys(r)) keys.add(k);
  // ⚠ RE-STAMPED. The rows carry the id of whoever took the backup, and RLS
  // rejects an insert whose `user_id` is not the caller. Beta does the same
  // thing for the same reason — its state has no user id at all and
  // `saveStateToCloud` stamps the signed-in one on the way out.
  const hasUser = keys.has('user_id');
  return rows.map((r) => {
    const out: Record<string, unknown> = {};
    for (const k of keys) out[k] = k in r ? r[k] : null;
    if (hasUser) out['user_id'] = userId;
    return out;
  });
}

/**
 * Replace the book with the file's contents.
 *
 * Upserts every row the file holds, then removes the rows it does not — per
 * table, parents first. See the header for why that order and not the other one.
 */
export async function restoreFromExport(
  sb: SupabaseClient,
  user: { id: string },
  file: ValidatedFile,
): Promise<WriteReport> {
  const report: WriteReport = { written: 0, pruned: 0, failures: [] };

  for (const entry of file.plan) {
    const key = KEY_OF[entry.table] ?? 'id';
    try {
      const rows = normaliseRows(entry.rows, user.id);

      for (let i = 0; i < rows.length; i += CHUNK) {
        const chunk = rows.slice(i, i + CHUNK);
        const { error } = await sb.from(entry.table).upsert(chunk, { onConflict: key });
        if (error) throw new Error(error.message);
        report.written += chunk.length;
      }

      // ── The prune, which is the destructive half and runs LAST ──
      const keep = new Set(rows.map((r) => String(r[key])));
      const present = await existingIds(sb, entry.table, key);
      const drop = present.filter((id) => !keep.has(id));
      for (let i = 0; i < drop.length; i += CHUNK) {
        const chunk = drop.slice(i, i + CHUNK);
        const { error } = await sb.from(entry.table).delete().in(key, chunk);
        if (error) throw new Error(error.message);
        report.pruned += chunk.length;
      }
    } catch (e) {
      // ⚠ ONE TABLE'S FAILURE DOES NOT ABORT THE REST, and the reason is the
      // one in the header: after a failure the user holds a superset, and
      // finishing the remaining tables moves them closer to the file rather
      // than further from it. What must not happen is calling it a success.
      report.failures.push({
        table: entry.table,
        message: e instanceof Error ? e.message : 'write failed',
      });
    }
  }

  if (report.failures.length) {
    record({
      kind: 'error',
      msg: `restore account: ${report.failures.length} of ${file.plan.length} tables failed `
        + `(${report.failures.map((f) => f.table).join(', ')})`,
      stack: null,
      source: null,
    });
  }
  return report;
}

export interface ResetReport {
  /** Tables cleared without error. */
  cleared: string[];
  /** Tables that did not clear. EMPTY IS THE ONLY SUCCESS. */
  failures: TableFailure[];
}

/**
 * Erase the whole book, keeping the account and its logger keys.
 *
 * ⚠ CHILDREN FIRST. `ON DELETE CASCADE` and `SET NULL` would cover most of this
 * on their own, but relying on them means the delete order encodes a schema
 * detail nothing here asserts — and `account_adjustments` cascading from
 * `accounts` is exactly the surprise `deleteAccount.ts` exists to warn about.
 *
 * ⚠ `.not(key, 'is', null)` is a WHERE that matches every row rather than a bare
 * `delete()`, which PostgREST refuses without a filter. RLS is what scopes it to
 * the caller; this only exists to satisfy the "no unfiltered delete" rule.
 */
export async function resetEverything(
  sb: SupabaseClient,
): Promise<ResetReport> {
  const report: ResetReport = { cleared: [], failures: [] };

  for (const table of WIPE_ORDER) {
    const key = KEY_OF[table] ?? 'id';
    try {
      const { error } = await sb.from(table).delete().not(key, 'is', null);
      if (error) throw new Error(error.message);
      report.cleared.push(table);
    } catch (e) {
      report.failures.push({
        table,
        message: e instanceof Error ? e.message : 'delete failed',
      });
    }
  }

  if (report.failures.length) {
    reportFailureAs(
      'reset everything',
      report.failures[0],
      `${report.failures.length} of ${WIPE_ORDER.length} tables could not be cleared `
        + `(${report.failures.map((f) => f.table).join(', ')})`,
    );
  }
  return report;
}

/**
 * ⚠ Asserted by a test rather than trusted: every table the export writes is
 * either restored or explicitly skipped with a reason. A table added to
 * `EXPORT_TABLES` and forgotten here would be downloaded and never restored —
 * silently, which is the failure mode both files were written against.
 */
export function unhandledTables(): string[] {
  const handled = new Set([...RESTORE_ORDER, ...SKIPPED]);
  return EXPORT_TABLES.map((t) => t.table).filter((t) => !handled.has(t));
}
